/**
 * snapshot.ts — render the finished piece to a PNG data URL.
 *
 * Redraws the build (ring + closure + placed charms) onto an offscreen <canvas>
 * using the SAME geometry as the live stage, so it matches what the customer
 * sees. Images are loaded crossOrigin so the canvas stays exportable (Shopify CDN
 * serves CORS headers); if anything taints it, we return null and the caller falls
 * back to the chain image. Used as the grouped line's thumbnail in the cart drawer.
 */

import { tToXY, tToHangRotation, SIZE_CM, REF_PIECE_CM, type CharmSize } from './geometry';
import { ringCharms, BASE_LAYOUT, type BuildState } from './state';
import { closureImage, type Catalog } from './data';
import { beadsLayoutFor, slotPx, slotAngleDeg } from './slots';

// Mirror the canvas/step3 render tunables.
const RING_IMG_SCALE = 1.1;
const CLOSURE_SCALE  = 0.16;
const CLOSURE_OFFSET = 0.006;
const CHARM_SCALE    = 1;
// Per-base charm/closure size multipliers come from catalog.sizes (mirror canvas.ts).

const R       = 460;   // logical ring radius for the snapshot (higher = crisper)
const MARGIN  = 48;    // padding around the piece bounds
const MAX_OUT = 1400;  // output max dimension (px) — high-res for the admin/order

function loadImg(src: string): Promise<HTMLImageElement | null> {
  return new Promise(resolve => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

const charmPx = (size: CharmSize, baseScale = 1) => (SIZE_CM[size] / REF_PIECE_CM) * (2 * Math.PI * R) * CHARM_SCALE * baseScale;

/**
 * Crop a canvas to its non-transparent bounding box (+ a small pad) so the rendered piece —
 * not the padded/asymmetric canvas — sits centred when shown object-contain in a card. Returns
 * null if the canvas is empty or pixel data can't be read (CORS-tainted; caller keeps original).
 */
function trimToContent(src: HTMLCanvasElement, pad = 12): HTMLCanvasElement | null {
  const ctx = src.getContext('2d');
  if (!ctx) return null;
  const w = src.width, h = src.height;
  let data: Uint8ClampedArray;
  try { data = ctx.getImageData(0, 0, w, h).data; } catch { return null; }
  let minX = w, minY = h, maxX = -1, maxY = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (data[(y * w + x) * 4 + 3] > 8) { // alpha threshold (ignore near-transparent edges)
        if (x < minX) minX = x; if (x > maxX) maxX = x;
        if (y < minY) minY = y; if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < minX || maxY < minY) return null; // nothing drawn
  minX = Math.max(0, minX - pad); minY = Math.max(0, minY - pad);
  maxX = Math.min(w - 1, maxX + pad); maxY = Math.min(h - 1, maxY + pad);
  const cw = maxX - minX + 1, ch = maxY - minY + 1;
  const out = document.createElement('canvas');
  out.width = cw; out.height = ch;
  out.getContext('2d')?.drawImage(src, minX, minY, cw, ch, 0, 0, cw, ch);
  return out;
}

/**
 * Draw the build and return a PNG data URL, or null if nothing/tainted.
 * `maxOut` caps the output dimension: the default (1400) is the high-res image for
 * cart/order/admin; favorites pass a small value (~360) so many thumbnails fit the
 * ~5MB localStorage budget.
 */
export async function capturePiece(catalog: Catalog, build: BuildState, maxOut = MAX_OUT): Promise<string | null> {
  if (BASE_LAYOUT[build.base] === 'beads') return captureBeads(catalog, build, maxOut);
  const chain = catalog.chains.find(c => c.id === build.chain?.id) ?? null;
  const placed = ringCharms(build);

  // Preload all images crossOrigin (so toDataURL doesn't throw on a tainted canvas).
  const ringImg = chain?.image ? await loadImg(chain.image) : null;
  const closureSrc = closureImage(catalog, build.closure);
  const closureI = closureSrc ? await loadImg(closureSrc) : null;
  const charmImgs = await Promise.all(placed.map(c => {
    const item = catalog.charms.find(ci => ci.productId === c.productId);
    return item?.image ? loadImg(item.image) : Promise.resolve(null);
  }));

  const baseScale = catalog.sizes.charm[build.base]; // per-base charm size multiplier (section range)
  // Canvas bounds: the ring image, plus charms that hang one diameter outward.
  let maxD = 0;
  for (const c of placed) maxD = Math.max(maxD, charmPx(c.size, baseScale));
  const half = Math.max(R * RING_IMG_SCALE, R + maxD) + MARGIN;
  const S = Math.ceil(half * 2);

  const canvas = document.createElement('canvas');
  canvas.width = S; canvas.height = S;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  const ring = { cx: S / 2, cy: S / 2, radius: R };

  // Chain ring (below charms).
  if (ringImg) {
    const rs = R * 2 * RING_IMG_SCALE;
    ctx.drawImage(ringImg, ring.cx - rs / 2, ring.cy - rs / 2, rs, rs);
  }
  // Charms: pivot at the ring attach point (top-centre), rotate by the hang angle,
  // draw the body hanging outward — matches the DOM (orient transform-origin 50% 0%).
  placed.forEach((c, i) => {
    const img = charmImgs[i];
    if (!img) return;
    const d = charmPx(c.size, baseScale);
    const a = tToXY(c.t as number, ring);
    // object-contain within the d×d box (preserve aspect ratio; centre in the box).
    const iw = img.naturalWidth || d;
    const ih = img.naturalHeight || d;
    const s = Math.min(d / iw, d / ih);
    const dw = iw * s, dh = ih * s;
    ctx.save();
    ctx.translate(a.x, a.y);
    ctx.rotate(tToHangRotation(c.t as number));
    ctx.drawImage(img, -dw / 2, (d - dh) / 2, dw, dh);
    ctx.restore();
  });
  // Closure on top (centred on the t=0 anchor, nudged down like the stage).
  if (closureI) {
    const a0 = tToXY(0, ring);
    const cw = R * 2 * (build.base === 'keychain' ? catalog.sizes.closure.keychain : CLOSURE_SCALE);
    const ch = closureI.naturalWidth ? cw * (closureI.naturalHeight / closureI.naturalWidth) : cw;
    ctx.drawImage(closureI, a0.x - cw / 2, (a0.y + R * CLOSURE_OFFSET) - ch / 2, cw, ch);
  }

  // Downscale for a compact data URL.
  let out: HTMLCanvasElement = canvas;
  if (S > maxOut) {
    const o = document.createElement('canvas');
    o.width = maxOut; o.height = maxOut;
    const octx = o.getContext('2d');
    if (octx) { octx.drawImage(canvas, 0, 0, maxOut, maxOut); out = o; }
  }
  try { return out.toDataURL('image/png'); }
  catch { return null; } // CORS-tainted → caller falls back to the chain image
}

/**
 * Bag charm ("beads") snapshot: the beaded-chain base IMAGE at its natural aspect, with
 * the carabiner closure at both ends and charms attached at their pinned slots (anchor on
 * the dot, rotated by the slot angle) — mirrors the canvas beads renderer (slots.ts).
 */
async function captureBeads(catalog: Catalog, build: BuildState, maxOut: number): Promise<string | null> {
  const cfg = beadsLayoutFor(build.base);                  // per-base slots / closures / focal
  const BEADS_CHARM_SCALE = catalog.sizes.charm[build.base] * (build.base === 'keychain' ? 2 : 1); // mirror canvas.ts
  const closureScale = build.base === 'keychain' ? catalog.sizes.closure.keychain : catalog.sizes.closure.bag_charm;
  const chain = catalog.chains.find(c => c.id === build.chain?.id) ?? null;
  const placed = ringCharms(build); // (placed = non-null t; here t is a slot index)

  const baseImg = chain?.image ? await loadImg(chain.image) : null;
  const closureSrc = closureImage(catalog, build.closure);
  const closureI = closureSrc ? await loadImg(closureSrc) : null;
  const charmImgs = await Promise.all(placed.map(c => {
    const item = catalog.charms.find(ci => ci.productId === c.productId);
    return item?.image ? loadImg(item.image) : Promise.resolve(null);
  }));

  // Logical base size from the PNG aspect; charms sized as a fraction of the base width.
  const BASE_W = 1400;
  const aspect = baseImg && baseImg.naturalWidth ? baseImg.naturalHeight / baseImg.naturalWidth : 0.65;
  const BASE_H = BASE_W * aspect;
  const cpx = (size: CharmSize) => (SIZE_CM[size] / REF_PIECE_CM) * BASE_W * BEADS_CHARM_SCALE;

  let maxD = 0;
  for (const c of placed) maxD = Math.max(maxD, cpx(c.size));
  const margin = maxD + MARGIN; // charms can dangle beyond the base bounds
  const W = Math.ceil(BASE_W + margin * 2);
  const H = Math.ceil(BASE_H + margin * 2);
  const box = { left: margin, top: margin, width: BASE_W, height: BASE_H };

  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  // Carabiner closures FIRST so they sit BEHIND the chain (top-centre on the dot, rotated).
  if (closureI) {
    const cw = box.width * closureScale;
    const ch = closureI.naturalWidth ? cw * (closureI.naturalHeight / closureI.naturalWidth) : cw;
    cfg.closures.forEach(slot => {
      const p = slotPx(slot, box);
      ctx.save();
      ctx.translate(p.x, p.y);                       // pivot at the dot
      ctx.rotate(((slot.angle ?? 0) * Math.PI) / 180);
      ctx.drawImage(closureI, -cw / 2, 0, cw, ch);   // top-centre on the dot
      ctx.restore();
    });
  }

  if (baseImg) ctx.drawImage(baseImg, box.left, box.top, box.width, box.height);

  placed.forEach((c, i) => {
    const img = charmImgs[i];
    if (!img) return;
    const slot = cfg.slots[c.t as number];
    if (!slot) return;
    const d = cpx(c.size);
    const p = slotPx(slot, box);
    const iw = img.naturalWidth || d;
    const ih = img.naturalHeight || d;
    const s = Math.min(d / iw, d / ih);
    const dw = iw * s, dh = ih * s;
    ctx.save();
    ctx.translate(p.x, p.y);                                       // pivot at the slot dot (charm's top anchor)
    ctx.rotate((slotAngleDeg(slot, cfg.focal) * Math.PI) / 180);   // orient toward the focal (outward)
    ctx.drawImage(img, -dw / 2, (d - dh) / 2, dw, dh);
    ctx.restore();
  });

  // Trim the transparent margins so the piece (not the padded canvas) centres in the card.
  const trimmed = trimToContent(canvas) ?? canvas;
  // Downscale so the larger side caps at maxOut (preserve aspect).
  let out: HTMLCanvasElement = trimmed;
  const big = Math.max(trimmed.width, trimmed.height);
  if (big > maxOut) {
    const k = maxOut / big;
    const o = document.createElement('canvas');
    o.width = Math.round(trimmed.width * k); o.height = Math.round(trimmed.height * k);
    const octx = o.getContext('2d');
    if (octx) { octx.drawImage(trimmed, 0, 0, o.width, o.height); out = o; }
  }
  try { return out.toDataURL('image/png'); }
  catch { return null; }
}
